/**
 * @name useQZTray
 * @author Keven Adan Bezerra | kevenadanb
 * @description Hook customizado para gerenciar integração com QZ Tray
 * @returns {QZTrayHookReturn} Estados e métodos para controle do QZ Tray
 * @package src/hooks/useQZTray.ts
 */

import { useCallback, useEffect, useState } from "react";
import {
  configureQZSecurity,
  eliminateQZTrayDialogs,
  loadJSRSASignLibrary,
  QZ_DEMO_CERT_INFO,
  setupCustomCertificateTrust,
  verifyCertificateConfiguration,
} from "../utils/qzClientSideSigning";
import { useAlert } from "./useAlert";
import { useLoading } from "./useLoading";

// Tipos para o QZ Tray
interface QZPrinter {
  name: string;
  description?: string;
}

interface QZTrayHookReturn {
  // Estados
  isConnected: boolean;
  isConnecting: boolean;
  printers: QZPrinter[];
  selectedPrinter: string | null;

  // Métodos
  connectToQZTray: () => Promise<boolean>;
  getPrinters: () => Promise<QZPrinter[]>;
  setDefaultPrinter: (printerName: string) => void;
  testPrint: (printerName?: string) => Promise<boolean>;
  isQZTrayInstalled: () => boolean;
  getQZTrayDownloadUrl: () => string;
  getCertificateInfo: () => typeof QZ_DEMO_CERT_INFO;
}

// Declaração global para qz
declare global {
  interface Window {
    qz: any;
  }
}

/**
 * Hook para gerenciar integração com QZ Tray
 */
export const useQZTray = (): QZTrayHookReturn => {
  const { showSuccess, showError, showWarning } = useAlert();
  const { withGlobalLoading, setLoadingState } = useLoading();

  // Estados
  const [isConnected, setIsConnected] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [printers, setPrinters] = useState<QZPrinter[]>([]);
  const [selectedPrinter, setSelectedPrinter] = useState<string | null>(null);

  /**
   * Verifica se QZ Tray está instalado e acessível
   */
  const isQZTrayInstalled = useCallback((): boolean => {
    return typeof window !== "undefined" && window.qz !== undefined;
  }, []);

  /**
   * Retorna URL de download do QZ Tray
   */
  const getQZTrayDownloadUrl = useCallback((): string => {
    return "https://github.com/qzind/tray/releases/download/v2.2.5/qz-tray-2.2.5-x86_64.exe";
  }, []);

  /**
   * Conecta ao QZ Tray com configuração correta para evitar diálogos
   */
  const connectToQZTray = useCallback(async (): Promise<boolean> => {
    if (!isQZTrayInstalled()) {
      showError("QZ Tray não está instalado ou não foi carregado");
      return false;
    }

    setIsConnecting(true);

    try {
      console.log("🔐 Iniciando conexão QZ Tray com Client-Side Signing...");

      // 0. PRIMEIRO: Carregar jsrsasign library
      console.log("📋 Passo 0: Carregando jsrsasign library...");
      const jsrsasignLoaded = await loadJSRSASignLibrary();
      if (!jsrsasignLoaded) {
        console.warn("⚠️ jsrsasign não pôde ser carregada, usando fallback");
      }

      // 1. SEGUNDO: Eliminar diálogos antes de qualquer configuração
      console.log("📋 Passo 1: Eliminando diálogos QZ Tray...");
      await eliminateQZTrayDialogs(window.qz);

      // 1.5 ADICIONAL: Configurar confiança para certificados customizados
      console.log(
        "📋 Passo 1.5: Configurando confiança para certificados customizados...",
      );
      await setupCustomCertificateTrust(window.qz);

      // 2. TERCEIRO: Configurar certificados com Client-Side Signing
      console.log(
        "📋 Passo 2: Configurando certificados com Client-Side Signing...",
      );
      await configureQZSecurity(window.qz);

      // 3. QUARTO: Verificar configuração
      console.log("📋 Passo 3: Verificando configuração QZ Tray Demo Cert...");
      const isConfigValid = await verifyCertificateConfiguration();
      if (!isConfigValid) {
        console.warn(
          "⚠️ Configuração QZ Tray Demo Cert pode não estar completa",
        );
      }

      // 4. QUINTO: Conectar ao websocket
      console.log("📋 Passo 4: Conectando ao websocket QZ Tray...");
      if (!window.qz.websocket.isActive()) {
        await window.qz.websocket.connect();
      }

      console.log(
        "✅ QZ Tray conectado com Client-Side Signing:",
        QZ_DEMO_CERT_INFO,
      );
      setIsConnected(true);
      showSuccess("Conectado ao QZ Tray com Client-Side Signing");

      // Informar sobre autorização esperada
      console.log("ℹ️ Client-Side Signing configurado com jsrsasign library.");

      return true;
    } catch (error: any) {
      // Apenas log no console - impressora é opcional
      console.warn(
        "⚠️ QZ Tray não disponível (impressão opcional):",
        error?.message || error,
      );
      console.info(
        'ℹ️ Se aparecer diálogo de certificado do QZ Tray, clique em "Allow".',
      );
      setIsConnected(false);
      return false;
    } finally {
      setIsConnecting(false);
    }
  }, [isQZTrayInstalled, showSuccess, showError]);

  /**
   * Busca lista de impressoras disponíveis e define automaticamente uma padrão
   */
  const getPrinters = useCallback(async (): Promise<QZPrinter[]> => {
    if (!isConnected) {
      const connected = await connectToQZTray();
      if (!connected) return [];
    }

    try {
      setLoadingState("qz-printers", true);

      const printerList = await window.qz.printers.find();
      const formattedPrinters: QZPrinter[] = printerList.map(
        (printer: string) => ({
          name: printer,
          description: printer,
        }),
      );

      setPrinters(formattedPrinters);

      // 🎯 Auto-seleção inteligente de impressora
      if (formattedPrinters.length > 0 && !selectedPrinter) {
        let autoSelectedPrinter = null;

        // 1. Buscar impressora padrão do sistema
        try {
          const defaultPrinter = await window.qz.printers.getDefault();
          if (
            defaultPrinter &&
            formattedPrinters.find((p) => p.name === defaultPrinter)
          ) {
            autoSelectedPrinter = defaultPrinter;
            console.log(
              "✅ Impressora padrão do sistema detectada:",
              defaultPrinter,
            );
          }
        } catch (defaultError) {
          console.log(
            "ℹ️ Não foi possível detectar impressora padrão:",
            defaultError,
          );
        }

        // 2. Se não encontrou padrão, buscar "Microsoft Print to PDF"
        if (!autoSelectedPrinter) {
          const pdfPrinter = formattedPrinters.find(
            (p) =>
              p.name.toLowerCase().includes("microsoft print to pdf") ||
              p.name.toLowerCase().includes("print to pdf") ||
              p.name.toLowerCase().includes("pdf"),
          );
          if (pdfPrinter) {
            autoSelectedPrinter = pdfPrinter.name;
            console.log(
              "✅ Microsoft Print to PDF detectado:",
              pdfPrinter.name,
            );
          }
        }

        // 3. Se ainda não encontrou, usar a primeira impressora disponível
        if (!autoSelectedPrinter) {
          autoSelectedPrinter = formattedPrinters[0].name;
          console.log(
            "✅ Primeira impressora disponível selecionada:",
            autoSelectedPrinter,
          );
        }

        // Definir impressora automaticamente
        if (autoSelectedPrinter) {
          setSelectedPrinter(autoSelectedPrinter);
          localStorage.setItem("qz-default-printer", autoSelectedPrinter);
          showSuccess(
            `Impressora "${autoSelectedPrinter}" selecionada automaticamente`,
          );
        }
      }

      return formattedPrinters;
    } catch (error) {
      console.error("Erro ao buscar impressoras:", error);
      showError("Erro ao buscar impressoras disponíveis");
      return [];
    } finally {
      setLoadingState("qz-printers", false);
    }
  }, [isConnected, connectToQZTray, showError, setLoadingState]);

  /**
   * Define impressora padrão
   */
  const setDefaultPrinter = useCallback(
    (printerName: string): void => {
      setSelectedPrinter(printerName);
      localStorage.setItem("qz-default-printer", printerName);
      showSuccess(`Impressora "${printerName}" definida como padrão`);
    },
    [showSuccess],
  );

  /**
   * Testa impressão
   */
  const testPrint = useCallback(
    async (printerName?: string): Promise<boolean> => {
      const printer = printerName || selectedPrinter;

      if (!printer) {
        showWarning("Nenhuma impressora selecionada");
        return false;
      }

      if (!isConnected) {
        const connected = await connectToQZTray();
        if (!connected) return false;
      }

      return withGlobalLoading(async () => {
        try {
          const config = window.qz.configs.create(printer);

          const data = [
            "Teste de Impressao\n",
            "Sagrada Familia - Sistema de Totem\n",
            "Data/Hora: " + new Date().toLocaleString("pt-BR") + "\n",
            "Impressora: " + printer + "\n",
            "Status: Funcionando corretamente\n",
            "\n",
            "Este e um teste de impressao.\n",
          ];

          await window.qz.print(config, data);
          showSuccess("Teste de impressao enviado com sucesso");
          return true;
        } catch (error) {
          console.error("Erro no teste de impressao:", error);
          showError("Erro ao enviar teste de impressao");
          return false;
        }
      });
    },
    [
      selectedPrinter,
      isConnected,
      connectToQZTray,
      showWarning,
      showSuccess,
      showError,
      withGlobalLoading,
    ],
  );

  /**
   * Retorna informações sobre o certificado QZ Demo Cert configurado
   */
  const getCertificateInfo = useCallback((): typeof QZ_DEMO_CERT_INFO => {
    return QZ_DEMO_CERT_INFO;
  }, []);

  /**
   * Carrega impressora padrão salva no localStorage
   */
  useEffect(() => {
    const savedPrinter = localStorage.getItem("qz-default-printer");
    if (savedPrinter) {
      setSelectedPrinter(savedPrinter);
    }
  }, []);

  /**
   * Tentativa automática de conexão ao montar o hook
   */
  useEffect(() => {
    if (isQZTrayInstalled() && !isConnected && !isConnecting) {
      connectToQZTray().catch(console.error);
    }
  }, [isQZTrayInstalled, isConnected, isConnecting, connectToQZTray]);

  return {
    // Estados
    isConnected,
    isConnecting,
    printers,
    selectedPrinter,

    // Métodos
    connectToQZTray,
    getPrinters,
    setDefaultPrinter,
    testPrint,
    isQZTrayInstalled,
    getQZTrayDownloadUrl,
    getCertificateInfo,
  };
};
